import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    TextField,
    CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { CiClock2 } from 'react-icons/ci';
import api from '../../api/user';
import { toast } from 'react-toastify';
import { Check, X } from '@mui/icons-material';
import { formatTime } from '../../utils/helper';

const ViewTest = () => {
    const [data, setData] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [fetchingData, setFetchingData] = useState(true);
    const [OpenModal, setOpenModal] = useState(false);
    const [score, setScore] = useState('');
    const [viewedQuestions, setViewedQuestions] = useState(new Set([0]));
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const user = JSON.parse(localStorage.lasmabObj) || {};
    const token = localStorage.lasmabAuthToken || '';
    const { subject: encodedSubject, subjectId, title: encodedTitle, testId, duration } = useParams();

    const totalDuration = Number(duration) * 60;
    const [timeLeft, setTimeLeft] = useState(totalDuration);

    const subject = decodeURIComponent(encodedSubject || '');
    const title = decodeURIComponent(encodedTitle || '');

    const navigate = useNavigate();

    const getCBTQuestions = useCallback(async () => {
        try {
            setFetchingData(true);
            const res = await api.getStudentCBTAllQuestions(token, user.schoolId, user.classId, subjectId, Number(testId));
            if (res.status && res.statusCode === 2) {
                setData(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFetchingData(false);
        }
    }, [token, user.schoolId, subjectId]);

    useEffect(() => {
        getCBTQuestions();
    }, [getCBTQuestions]);

    const handleAnswerChange = (questionId, value, type) => {
        setAnswers((prev) => {
            if (type === "M") {
                const existing = prev[questionId]?.selectedOption || [];
                const newOptions = existing.includes(value)
                    ? existing.filter((opt) => opt !== value)
                    : [...existing, value];
                return {
                    ...prev,
                    [questionId]: { questionId, selectedOption: newOptions, selectedAnswer: "" }
                };
            }

            if (type === "F") {
                return {
                    ...prev,
                    [questionId]: { questionId, selectedOption: "", selectedAnswer: value }
                };
            }

            return {
                ...prev,
                [questionId]: { questionId, selectedOption: value, selectedAnswer: "" }
            };
        });
    };

    const handleNextQuestion = () => {
        const currentQuestion = data[currentQuestionIndex];
        if (!answers[currentQuestion.questionId]) {
            alert('Please answer before continuing.');
            return;
        }
        const nextIndex = currentQuestionIndex + 1;
        if (nextIndex < data.length) {
            setCurrentQuestionIndex(nextIndex);
            setViewedQuestions((prev) => new Set([...prev, nextIndex]));
        }
    };

    const handleQuestionNavigation = (index) => {
        setCurrentQuestionIndex(index);
        setViewedQuestions((prev) => new Set([...prev, index]));
    };

    useEffect(() => {
        if (timeLeft <= 0) {
            handleSubmit();
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);


    const handleSubmit = async () => {
        const payload = {
            setupId: Number(testId),
            studentId: user.studentId,
            schoolId: user.schoolId,
            cbtAnswers: Object.values(answers).map((ans) => ({
                questionId: ans.questionId,
                selectedOption: Array.isArray(ans.selectedOption)
                    ? ans.selectedOption.join(",")
                    : ans.selectedOption ?? "",
                selectedAnswer: ans.selectedAnswer ?? ""
            }))
        };

        try {
            setSubmitting(true);

            const res = await api.submitCBTQuestions(token, payload);
            if (res.status && res.statusCode === 2) {
                toast.success(`Your Score: ${res?.data?.myScore}`);
                setScore(res?.data?.myScore);
                setOpenModal(true);
            } else {
                toast.error(res.message || "Submission failed");
            }
        } catch (error) {
            console.error("Submit error:", error);
            toast.error(error.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };


    const currentQuestion = data[currentQuestionIndex] || {};
    const totalQuestions = data.length;
    const options = currentQuestion.options || {};
    const allAnswered = Object.keys(answers).length === totalQuestions;
    const selectedAnswer = answers[currentQuestion.questionId] || { selectedOption: [], selectedAnswer: "" };

    useEffect(() => {
        const q = data[currentQuestionIndex];
        if (q && !answers[q.questionId]) {
            setAnswers((prev) => ({
                ...prev,
                [q.questionId]: { questionId: q.questionId, selectedOption: [], selectedAnswer: "" },
            }));
        }
    }, [currentQuestionIndex, data, answers]);


    if (fetchingData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={60} sx={{ color: '#671E78' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <div
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 font-medium text-sm cursor-pointer hover:underline"
            >
                ← Go Back
            </div>
            <Box sx={{ pb: 12 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        padding: '20px',
                        gap: '20px'
                    }}
                >
                    {data.length > 0 ?
                        <>
                            <Box sx={{ flex: 2 }}>
                                <h1 className="font-bold text-2xl mb-4">CBT</h1>
                                <div className="p-5 flex justify-between rounded-lg bg-gray-200 shadow-sm mb-8">
                                    <div>
                                        <h1 className="font-bold text-lg">{subject}</h1>
                                        <div className="flex items-center gap-3 pt-12 text-sm">
                                            <span>
                                                <CiClock2 />
                                            </span>
                                            <span>{`${duration} mins` || '0 min'}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="font-bold text-lg">Total Questions: {totalQuestions}</p>
                                        <p className="px-6 py-1 text-sm mt-12 bg-white rounded-full">{title}</p>
                                    </div>
                                    <div className="text-gray-800 font-semibold text-sm text-center">
                                        ⏱ Time Left: {formatTime(timeLeft)}
                                    </div>

                                </div>

                                {/* QUESTION */}
                                {currentQuestion && (
                                    <Box key={currentQuestion.questionId} sx={{ mb: 4 }}>
                                        <Typography variant="h6" sx={{ fontFamily: 'Gilroy-Medium', mb: 2 }}>
                                            {currentQuestionIndex + 1}. {currentQuestion.question}
                                        </Typography>
                                        {currentQuestion.attachments && currentQuestion.attachments.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                {currentQuestion.attachments.map((file, index) => {
                                                    const fileUrl = file?.filePath
                                                    console.log(fileUrl);
                                                    return (
                                                        <Box key={index} sx={{ mt: 1 }}>
                                                            <img
                                                                src={fileUrl}
                                                                alt={`attachment-${index}`}
                                                                style={{
                                                                    height: "250px",
                                                                    width: "100%",
                                                                    borderRadius: "8px",
                                                                    marginTop: "8px",
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}

                                        {currentQuestion.answerType === 'F' ? (
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                placeholder="Enter your answer here"
                                                value={answers[currentQuestion.questionId]?.selectedAnswer || ''}
                                                onChange={(e) =>
                                                    handleAnswerChange(currentQuestion.questionId, e.target.value, 'F')
                                                }
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: '#671E78'
                                                        }
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontFamily: 'Gilroy-Regular', mb: 2, color: '#666' }}
                                                >
                                                    {currentQuestion.answerType === 'M'
                                                        ? '[Select multiple answers]'
                                                        : currentQuestion.answerType === 'S'
                                                            ? '[Select one answer]'
                                                            : '[Fill in the gap]'}
                                                </Typography>
                                                <Box sx={{ ml: 2 }}>
                                                    {['A', 'B', 'C', 'D', 'E'].map((option) =>
                                                        options[`option${option}`] ? (
                                                            <Box
                                                                key={option}
                                                                sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                                                            >
                                                                <Checkbox
                                                                    checked={
                                                                        currentQuestion.answerType === "M"
                                                                            ? selectedAnswer.selectedOption.includes(option)
                                                                            : selectedAnswer.selectedOption === option
                                                                    }
                                                                    onChange={() =>
                                                                        handleAnswerChange(
                                                                            currentQuestion.questionId,
                                                                            option,
                                                                            currentQuestion.answerType
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        color: '#671E78',
                                                                        '&.Mui-checked': {
                                                                            color: '#671E78'
                                                                        }
                                                                    }}
                                                                />

                                                                <Typography sx={{ fontFamily: 'Gilroy-Regular' }}>
                                                                    {options[`option${option}`]}
                                                                </Typography>
                                                            </Box>
                                                        ) : null
                                                    )}
                                                </Box>
                                            </>
                                        )}

                                        {/* NEXT BUTTON NOW BELOW QUESTION */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    textTransform: 'initial',
                                                    fontFamily: 'Gilroy-Regular',
                                                    backgroundColor: '#671E78',
                                                    '&:hover': {
                                                        backgroundColor: '#4e1661'
                                                    }
                                                }}
                                                onClick={handleNextQuestion}
                                                disabled={currentQuestionIndex === totalQuestions - 1}
                                            >
                                                Next →
                                            </Button>
                                        </Box>
                                    </Box>
                                )}

                            </Box>

                            {/* RIGHT SECTION */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                    backgroundColor: 'rgba(103, 30, 120, 0.1)',
                                    padding: '20px',
                                    borderRadius: '10px',
                                    marginTop: '46px',
                                    justifyContent: "space-between",
                                    height: "100%",
                                }}
                            >
                                <Box>
                                    <TableContainer
                                        component={Paper}
                                        sx={{
                                            mb: 3,
                                            boxShadow: 'none',
                                            border: '1px solid #e0e0e0',
                                            backgroundColor: '#ffffff'
                                        }}
                                    >
                                        <Table>
                                            <TableBody>
                                                {Array.from({ length: Math.ceil(totalQuestions / 5) }).map(
                                                    (_, row) => (
                                                        <TableRow
                                                            key={row}
                                                            sx={{ border: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}
                                                        >
                                                            {[0, 1, 2, 3, 4].map((col) => {
                                                                const questionNumber = row * 5 + col;
                                                                return questionNumber < totalQuestions ? (
                                                                    <TableCell
                                                                        key={col}
                                                                        align="center"
                                                                        sx={{
                                                                            border: '1px solid #e0e0e0',
                                                                            backgroundColor: '#ffffff',
                                                                            padding: '8px'
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            variant={
                                                                                viewedQuestions.has(questionNumber)
                                                                                    ? 'contained'
                                                                                    : 'outlined'
                                                                            }
                                                                            sx={{
                                                                                minWidth: 0,
                                                                                minHeight: 0,
                                                                                width: 30,
                                                                                height: 30,
                                                                                backgroundColor: viewedQuestions.has(questionNumber)
                                                                                    ? '#671E78'
                                                                                    : 'transparent',
                                                                                color: viewedQuestions.has(questionNumber)
                                                                                    ? '#fff'
                                                                                    : '#000',
                                                                                '&:hover': {
                                                                                    backgroundColor: viewedQuestions.has(questionNumber)
                                                                                        ? '#4e1661'
                                                                                        : 'rgba(103, 30, 120, 0.1)'
                                                                                }
                                                                            }}
                                                                            onClick={() => handleQuestionNavigation(questionNumber)}
                                                                        >
                                                                            {questionNumber + 1}
                                                                        </Button>
                                                                    </TableCell>
                                                                ) : (
                                                                    <TableCell
                                                                        key={col}
                                                                        sx={{
                                                                            border: '1px solid #e0e0e0',
                                                                            backgroundColor: '#ffffff'
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily: 'Gilroy-Regular', textAlign: 'center' }}
                                        >
                                            Answered questions
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'Gilroy-Medium',
                                                textAlign: 'center',
                                                fontSize: '22px',
                                                color: 'green'
                                            }}
                                        >
                                            {Object.keys(answers).length} of {totalQuestions}
                                        </Typography>
                                    </Box>
                                </Box>
                                {/* SUBMIT BUTTON FIXED */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        disabled={!allAnswered || submitting}
                                        sx={{
                                            textTransform: 'initial',
                                            fontFamily: 'Gilroy-Regular',
                                            backgroundColor: '#671E78',
                                            '&:hover': {
                                                backgroundColor: '#4e1661'
                                            }
                                        }}
                                        onClick={handleSubmit}
                                    >
                                        {submitting ? "Submitting.." : "Submit Test"}
                                    </Button>
                                </Box>
                            </Box>
                        </>
                        :
                        (
                            <div>
                                <h1 className="font-bold text-2xl mb-4">CBT</h1>
                                <div className="p-5 flex justify-between rounded-lg bg-gray-200 shadow-sm mb-8">
                                    <div>
                                        <h1 className="font-bold text-lg">{subject}</h1>
                                        <div className="flex items-center gap-3 pt-12 text-sm">
                                            <span>
                                                <CiClock2 />
                                            </span>
                                            <span>{`${duration} mins` || '0 min'}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="font-bold text-lg">Total Questions: {totalQuestions}</p>
                                        <p className="px-6 py-1 text-sm mt-12 bg-white rounded-full">{title}</p>
                                    </div>
                                </div>
                                <p className="text-center mt-7">No Available question</p>
                            </div>
                        )
                    }
                </Box>
                {OpenModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-lg w-full max-w-md relative p-6">
                            <button
                                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 border border-gray-500 rounded-full p-1 h-7 w-7 flex justify-center items-center cursor-pointer"
                                onClick={() => {
                                    setOpenModal(false);
                                    navigate(-1);
                                }}
                            >
                                <span className='text-lg'>X</span>
                            </button>
                            <div className="flex items-center justify-center bg-green-400 rounded-full h-40 w-40 my-5">
                                <div className="flex items-center justify-center bg-green-600 rounded-full h-28 w-28">
                                    <Check size={40} sx={{ color: "#fff", fontFamily: "bolder" }} />
                                </div>
                            </div>
                            <h2 className="text-lg font-semibold text-gray-900 text-center">
                                Test Completed
                            </h2>

                            <p className="text-purple-700 text-sm mt-2 text-center">Congratulations!</p>
                            <div className="p-6 rounded-lg w-full bg-purple-300 my-6">
                                <p className="text-xs text-gray-500 text-center">Your Score is</p>
                                <h1 className="text-[36px] font-bold text-center">{score}</h1>
                            </div>
                            <button
                                className='bg-purple-800 text-white text-sm w-full p-4 rounded-lg'
                                onClick={() => {
                                    setOpenModal(false)
                                    navigate(-1);
                                }}
                            >Okay</button>
                        </div>
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default ViewTest;